import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/lib/api'

export type TeamRole = 'owner' | 'admin' | 'member'

export interface TeamMember {
  userId: string
  email: string
  role: TeamRole
  joinedAt: string
}

export interface Team {
  teamId: string
  name: string
  workosOrgId: string
  ownerUserId: string
  members: TeamMember[]
  createdAt: string
  updatedAt: string
}

export function useTeam(teamId: string | undefined | null) {
  return useQuery({
    queryKey: ['team', teamId],
    queryFn: async () => {
      if (!teamId) return null
      return apiClient.get<Team>(`/api/v1/teams/${teamId}`)
    },
    enabled: !!teamId,
  })
}

export function useCreateTeam() {
  const queryClient = useQueryClient()
  return useMutation<
    { teamId: string; name: string; role: string },
    Error,
    { name: string }
  >({
    mutationFn: async input => {
      return apiClient.post('/api/v1/teams', input)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['auth'] })
      queryClient.invalidateQueries({ queryKey: ['team'] })
    },
  })
}

export function useUpdateTeam(teamId: string | null) {
  const queryClient = useQueryClient()
  return useMutation<{ teamId: string; name: string }, Error, { name: string }>(
    {
      mutationFn: async input => {
        if (!teamId) throw new Error('No teamId')
        return apiClient.patch(`/api/v1/teams/${teamId}`, input)
      },
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['team', teamId] })
      },
    },
  )
}

export function useInviteTeamMember(teamId: string | null) {
  const queryClient = useQueryClient()
  return useMutation<
    { userId: string; email: string; role: string },
    Error,
    { email: string; role?: 'admin' | 'member' }
  >({
    mutationFn: async input => {
      if (!teamId) throw new Error('No teamId')
      return apiClient.post(`/api/v1/teams/${teamId}/members`, input)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team', teamId] })
    },
  })
}

export function useRemoveTeamMember(teamId: string | null) {
  const queryClient = useQueryClient()
  return useMutation<{ success: boolean }, Error, string>({
    mutationFn: async (userId: string) => {
      if (!teamId) throw new Error('No teamId')
      return apiClient.delete(`/api/v1/teams/${teamId}/members/${userId}`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team', teamId] })
      queryClient.invalidateQueries({ queryKey: ['auth'] })
    },
  })
}
